/**
 * TimesheetsView.js — Hourly employee hours & punctuality
 *
 * Tabs:
 *   Hours        — per-employee punch list for the selected pay period,
 *                  weekly hour totals, OT flag (> 40 h/wk)
 *   Punctuality  — scheduled start vs actual clock-in, late count, avg delta
 *                  (cross-references schedules/{weekStart}/shifts with Timesheet)
 *
 * Only accessible to owner / manager.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  collection, getDocs, limit, onSnapshot, orderBy, query,
  Timestamp, where,
} from 'firebase/firestore';
import { db } from '../../firebase';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parsePeriod(id) {
  const parts = (id || '').split('_to_');
  if (parts.length < 2) return { label: id || '', startStr: '', endStr: '', startDate: null, endDate: null };
  const startDate = new Date(parts[0] + 'T00:00:00');
  const endDate   = new Date(parts[1] + 'T23:59:59');
  const mo        = { month: 'short', day: 'numeric' };
  return {
    startStr: parts[0],
    endStr:   parts[1],
    startDate,
    endDate,
    label: `${startDate.toLocaleDateString('en-US', mo)} – ${new Date(parts[1] + 'T00:00:00').toLocaleDateString('en-US', mo)}`,
  };
}

/** Monday-aligned week starts covering [startStr, endStr]. */
function getWeekStarts(startStr, endStr) {
  const starts = [];
  const first  = new Date(startStr + 'T00:00:00');
  const last   = new Date(endStr   + 'T00:00:00');
  // rewind to Monday of the week containing startStr
  const day    = first.getDay();
  let cur      = new Date(first);
  cur.setDate(first.getDate() - (day === 0 ? 6 : day - 1));
  while (cur <= last) {
    starts.push(cur.toISOString().slice(0, 10));
    cur = new Date(cur.getTime() + 7 * 86_400_000);
  }
  return starts;
}

/** ISO date string (YYYY-MM-DD) for the Monday containing a Timestamp. */
function weekKey(ts) {
  if (!ts) return '';
  const d   = ts.toDate ? ts.toDate() : new Date(ts);
  const day = d.getDay();
  const mon = new Date(d);
  mon.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return mon.toISOString().slice(0, 10);
}

function toDate(ts) {
  if (!ts) return null;
  return ts.toDate ? ts.toDate() : new Date(ts);
}

const fmtHrs = (n) => `${Number(n || 0).toFixed(1)}h`;

function fmtTime(ts) {
  const d = toDate(ts);
  if (!d) return '—';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function fmtDay(ts) {
  const d = toDate(ts);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtDayStr(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

/** Total unpaid meal-break minutes from a breaks array. */
function unpaidBreakMin(breaks = []) {
  let mins = 0;
  for (const b of breaks) {
    if (b.type === 'meal' && b.startedAt && b.endedAt) {
      mins += (toDate(b.endedAt) - toDate(b.startedAt)) / 60_000;
    }
  }
  return Math.round(mins);
}

/** Paid hours for a single timesheet entry (fallback if paidHours not stamped yet). */
function calcPaidHours(ts) {
  if (ts.paidHours != null) return ts.paidHours;
  if (!ts.clockOutTime) return null; // still open
  const raw = (toDate(ts.clockOutTime) - toDate(ts.clockInTime)) / 3_600_000;
  return Math.max(0, raw - unpaidBreakMin(ts.breaks || []) / 60);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatTile({ label, value, sub, accent }) {
  return (
    <div className={`bg-white border rounded-xl px-5 py-4 flex flex-col gap-0.5 ${accent || 'border-neutral-200'}`}>
      <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-neutral-800">{value}</p>
      {sub && <p className="text-xs text-neutral-400">{sub}</p>}
    </div>
  );
}

// ─── Hours tab ────────────────────────────────────────────────────────────────

function HoursTab({ rows, expandedId, setExpandedId }) {
  const totalHrs  = rows.reduce((s, r) => s + r.totalHours, 0);
  const otCount   = rows.filter(r => r.hasOT).length;
  const openCount = rows.filter(r => r.hasOpen).length;

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 border-2 border-dashed border-neutral-200 rounded-xl">
        <p className="text-sm text-neutral-400">No timesheet entries for this period.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Total Hours"      value={fmtHrs(totalHrs)} />
        <StatTile label="Employees"        value={rows.length} />
        <StatTile label="OT This Period"   value={otCount}
          accent={otCount > 0 ? 'border-amber-300' : 'border-neutral-200'}
          sub={otCount > 0 ? '>40 h in a week' : 'No OT'} />
        <StatTile label="Open Shifts"      value={openCount}
          accent={openCount > 0 ? 'border-blue-300' : 'border-neutral-200'}
          sub={openCount > 0 ? 'Currently clocked in' : 'All clocked out'} />
      </div>

      {/* Employee rows */}
      <div className="space-y-2">
        {rows.map(emp => {
          const open = expandedId === emp.empId;
          return (
            <div key={emp.empId} className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
              {/* Header */}
              <button
                onClick={() => setExpandedId(open ? null : emp.empId)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-neutral-800">{emp.displayName}</span>
                    {emp.position && (
                      <span className="text-xs text-neutral-400">{emp.position}</span>
                    )}
                    {emp.hasOT && (
                      <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">
                        OT
                      </span>
                    )}
                    {emp.hasOpen && (
                      <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200">
                        Clocked In
                      </span>
                    )}
                  </div>
                  {/* Weekly breakdown */}
                  <div className="flex gap-3 mt-0.5 flex-wrap">
                    {Object.entries(emp.weeklyHours).map(([wk, hrs]) => (
                      <span key={wk} className={`text-xs ${hrs > 40 ? 'text-amber-600 font-semibold' : 'text-neutral-400'}`}>
                        Wk of {new Date(wk + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: {fmtHrs(hrs)}{hrs > 40 ? ' ⚠' : ''}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-base font-bold text-neutral-800">{fmtHrs(emp.totalHours)}</p>
                  <p className="text-xs text-neutral-400">{emp.punches.length} shift{emp.punches.length !== 1 ? 's' : ''}</p>
                </div>
                <span className="text-neutral-300 text-sm ml-1">{open ? '▲' : '▼'}</span>
              </button>

              {/* Punch list */}
              {open && (
                <div className="border-t border-neutral-100">
                  {/* Column headers */}
                  <div className="grid text-xs font-semibold text-neutral-400 uppercase tracking-wide px-4 py-2 border-b border-neutral-100"
                    style={{ gridTemplateColumns: '1fr 80px 80px 60px 60px' }}>
                    <span>Date</span>
                    <span>Clock In</span>
                    <span>Clock Out</span>
                    <span>Break</span>
                    <span className="text-right">Paid</span>
                  </div>
                  {emp.punches.map(p => {
                    const paid     = p.paidHoursCalc;
                    const isOpen   = !p.clockOutTime;
                    const breakMin = unpaidBreakMin(p.breaks || []);
                    return (
                      <div key={p.id}
                        className="grid items-center px-4 py-2.5 border-b border-neutral-50 last:border-0 hover:bg-neutral-50"
                        style={{ gridTemplateColumns: '1fr 80px 80px 60px 60px' }}>
                        <span className="text-sm text-neutral-700">{fmtDay(p.clockInTime)}</span>
                        <span className="text-sm text-neutral-600">{fmtTime(p.clockInTime)}</span>
                        <span className={`text-sm ${isOpen ? 'text-blue-500 font-medium' : 'text-neutral-600'}`}>
                          {isOpen ? 'In progress' : fmtTime(p.clockOutTime)}
                        </span>
                        <span className="text-sm text-neutral-400">
                          {breakMin > 0 ? `${breakMin}m` : '—'}
                        </span>
                        <span className={`text-sm text-right font-semibold ${isOpen ? 'text-neutral-300' : 'text-neutral-800'}`}>
                          {isOpen ? '—' : fmtHrs(paid)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Punctuality tab ──────────────────────────────────────────────────────────

function PunctualityTab({ rows, loading, expandedId, setExpandedId }) {
  if (loading) {
    return <p className="text-sm text-neutral-400 text-center py-10">Loading schedule data…</p>;
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-neutral-200 rounded-xl gap-2">
        <p className="text-sm font-medium text-neutral-400">No schedule data for this period</p>
        <p className="text-xs text-neutral-300 max-w-xs text-center">
          Punctuality compares scheduled shift start times to actual clock-in times.
          Publish a schedule for this period to see results.
        </p>
      </div>
    );
  }

  const totalShifts   = rows.reduce((s, r) => s + r.compared, 0);
  const totalLate     = rows.reduce((s, r) => s + r.lateCount, 0);
  const totalNoShows  = rows.reduce((s, r) => s + r.noShows, 0);
  const pctOnTime     = totalShifts > 0 ? Math.round(((totalShifts - totalLate) / totalShifts) * 100) : null;

  return (
    <div className="space-y-4">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="On-Time Rate"  value={pctOnTime != null ? `${pctOnTime}%` : '—'}
          accent={pctOnTime != null && pctOnTime < 80 ? 'border-red-300' : 'border-neutral-200'} />
        <StatTile label="Shifts Compared" value={totalShifts} />
        <StatTile label="Late Arrivals"   value={totalLate}
          accent={totalLate > 0 ? 'border-amber-300' : 'border-neutral-200'}
          sub="> 5 min after scheduled" />
        <StatTile label="No-Shows"        value={totalNoShows}
          accent={totalNoShows > 0 ? 'border-red-300' : 'border-neutral-200'}
          sub="Scheduled, not clocked in" />
      </div>

      {/* Note */}
      <p className="text-xs text-neutral-400">
        "Late" = clocked in more than 5 minutes after scheduled shift start. Employees without scheduled shifts this period are excluded.
      </p>

      {/* Employee rows */}
      <div className="space-y-2">
        {rows.map(emp => {
          const open    = expandedId === emp.empId;
          const pct     = emp.compared > 0 ? Math.round(((emp.compared - emp.lateCount) / emp.compared) * 100) : null;
          const avgSign = emp.avgDelta != null ? (emp.avgDelta > 0 ? '+' : '') : '';
          return (
            <div key={emp.empId} className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setExpandedId(open ? null : emp.empId)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-neutral-800">{emp.displayName}</span>
                    {emp.position && <span className="text-xs text-neutral-400">{emp.position}</span>}
                    {emp.noShows > 0 && (
                      <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">
                        {emp.noShows} No-Show{emp.noShows > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-400 mt-0.5">
                    {emp.compared} shifts compared · {emp.lateCount} late
                    {emp.avgDelta != null ? ` · avg ${avgSign}${emp.avgDelta} min` : ''}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  {pct != null && (
                    <p className={`text-base font-bold ${pct >= 90 ? 'text-green-600' : pct >= 75 ? 'text-amber-600' : 'text-red-600'}`}>
                      {pct}%
                    </p>
                  )}
                  <p className="text-xs text-neutral-400">on time</p>
                </div>
                <span className="text-neutral-300 text-sm ml-1">{open ? '▲' : '▼'}</span>
              </button>

              {/* Per-day detail */}
              {open && (
                <div className="border-t border-neutral-100">
                  <div className="grid text-xs font-semibold text-neutral-400 uppercase tracking-wide px-4 py-2 border-b border-neutral-100"
                    style={{ gridTemplateColumns: '1fr 80px 80px 80px' }}>
                    <span>Date</span>
                    <span>Scheduled</span>
                    <span>Clocked In</span>
                    <span className="text-right">Delta</span>
                  </div>
                  {emp.days.sort((a, b) => a.date.localeCompare(b.date)).map(d => {
                    const isLate   = d.deltaMin != null && d.deltaMin > 5;
                    const isEarly  = d.deltaMin != null && d.deltaMin < -1;
                    const sign     = d.deltaMin != null ? (d.deltaMin > 0 ? '+' : '') : '';
                    return (
                      <div key={d.date}
                        className="grid items-center px-4 py-2.5 border-b border-neutral-50 last:border-0 hover:bg-neutral-50"
                        style={{ gridTemplateColumns: '1fr 80px 80px 80px' }}>
                        <span className="text-sm text-neutral-700">{fmtDayStr(d.date)}</span>
                        <span className="text-sm text-neutral-600">
                          {d.scheduledStart ? fmtTime({ toDate: () => d.scheduledStart }) : '—'}
                        </span>
                        <span className={`text-sm ${d.noShow ? 'text-red-500 font-medium' : 'text-neutral-600'}`}>
                          {d.noShow ? 'No-show' : (d.actualIn ? fmtTime({ toDate: () => d.actualIn }) : '—')}
                        </span>
                        <div className="flex justify-end">
                          {d.noShow ? (
                            <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">absent</span>
                          ) : d.deltaMin != null ? (
                            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded border ${
                              isLate  ? 'bg-amber-50 text-amber-700 border-amber-200' :
                              isEarly ? 'bg-green-50 text-green-700 border-green-200' :
                                        'bg-neutral-50 text-neutral-500 border-neutral-200'
                            }`}>
                              {sign}{d.deltaMin} min
                            </span>
                          ) : (
                            <span className="text-xs text-neutral-300">—</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const TimesheetsView = ({ restaurantId, user }) => {
  const [periods,        setPeriods]        = useState([]);
  const [selectedId,     setSelectedId]     = useState(null);
  const [timesheets,     setTimesheets]     = useState([]);
  const [employees,      setEmployees]      = useState([]);
  const [scheduleShifts, setScheduleShifts] = useState([]);
  const [loadingTS,      setLoadingTS]      = useState(false);
  const [loadingSched,   setLoadingSched]   = useState(false);
  const [activeTab,      setActiveTab]      = useState('hours');
  const [expandedId,     setExpandedId]     = useState(null);

  // ── Pay periods ─────────────────────────────────────────────────────────────
  useEffect(() => {
    return onSnapshot(
      query(
        collection(db, 'restaurants_data', restaurantId, 'payPeriods'),
        orderBy('startDate', 'desc'),
        limit(12),
      ),
      (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPeriods(list);
        if (list.length > 0) setSelectedId(prev => prev || list[0].id);
      },
    );
  }, [restaurantId]);

  // ── Employees (names / position) ────────────────────────────────────────────
  useEffect(() => {
    return onSnapshot(
      collection(db, 'restaurants_data', restaurantId, 'employees'),
      (snap) => setEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    );
  }, [restaurantId]);

  // ── Timesheets for selected period ──────────────────────────────────────────
  useEffect(() => {
    if (!selectedId) return;
    const { startDate, endDate } = parsePeriod(selectedId);
    if (!startDate) return;
    setLoadingTS(true);
    return onSnapshot(
      query(
        collection(db, 'restaurants_data', restaurantId, 'Timesheet'),
        where('clockInTime', '>=', Timestamp.fromDate(startDate)),
        where('clockInTime', '<=', Timestamp.fromDate(endDate)),
        orderBy('clockInTime', 'asc'),
      ),
      (snap) => {
        setTimesheets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoadingTS(false);
      },
      () => setLoadingTS(false),
    );
  }, [restaurantId, selectedId]);

  // ── Schedule shifts — only when punctuality tab is active ───────────────────
  useEffect(() => {
    if (activeTab !== 'punctuality' || !selectedId) return;
    const { startStr, endStr } = parsePeriod(selectedId);
    if (!startStr) return;
    setLoadingSched(true);
    const weekStarts = getWeekStarts(startStr, endStr);
    let cancelled    = false;
    Promise.all(
      weekStarts.map(ws =>
        getDocs(
          query(
            collection(db, 'restaurants_data', restaurantId, 'schedules', ws, 'shifts'),
            where('date', '>=', startStr),
            where('date', '<=', endStr),
          )
        ).then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })))
         .catch(() => [])
      )
    ).then(results => {
      if (!cancelled) {
        setScheduleShifts(results.flat());
        setLoadingSched(false);
      }
    });
    return () => { cancelled = true; };
  }, [restaurantId, selectedId, activeTab]);

  // ── Build employee map ───────────────────────────────────────────────────────
  const empById = useMemo(() => {
    const m = {};
    employees.forEach(e => { m[e.id] = e; });
    return m;
  }, [employees]);

  // ── Hours aggregation ────────────────────────────────────────────────────────
  const hoursRows = useMemo(() => {
    const map = {};
    timesheets.forEach(ts => {
      const key  = ts.serverID;
      const emp  = empById[key];
      const name = emp?.firstName
        ? `${emp.firstName} ${emp.lastName}`
        : (emp?.name || ts.name || key);
      if (!map[key]) map[key] = { empId: key, displayName: name, position: emp?.position || '', punches: [], weeklyHours: {} };
      const paid = calcPaidHours(ts);
      map[key].punches.push({ ...ts, paidHoursCalc: paid });
      if (paid != null) {
        const wk = weekKey(ts.clockInTime);
        map[key].weeklyHours[wk] = (map[key].weeklyHours[wk] || 0) + paid;
      }
    });
    return Object.values(map)
      .map(r => ({
        ...r,
        totalHours: r.punches.reduce((s, p) => s + (p.paidHoursCalc || 0), 0),
        hasOT:      Object.values(r.weeklyHours).some(h => h > 40),
        hasOpen:    r.punches.some(p => !p.clockOutTime),
      }))
      .sort((a, b) => b.totalHours - a.totalHours);
  }, [timesheets, empById]);

  // ── Punctuality aggregation ──────────────────────────────────────────────────
  const punctualityRows = useMemo(() => {
    if (!scheduleShifts.length) return [];

    // scheduled[empId_date] = earliest shift startTime
    const scheduled = {};
    scheduleShifts.forEach(s => {
      if (!s.employeeId || s.isOpenShift || !s.startTime) return;
      const key = `${s.employeeId}_${s.date}`;
      const t   = toDate(s.startTime);
      if (!scheduled[key] || t < scheduled[key]) scheduled[key] = t;
    });

    // clocked[empId_date] = earliest clockInTime
    const clocked = {};
    timesheets.forEach(ts => {
      if (!ts.clockInTime) return;
      const d       = toDate(ts.clockInTime);
      const dateStr = d.toISOString().slice(0, 10);
      const key     = `${ts.serverID}_${dateStr}`;
      if (!clocked[key] || d < clocked[key]) clocked[key] = d;
    });

    // Merge into per-employee days
    const empDays = {};
    Object.keys(scheduled).forEach(key => {
      const [empId, date] = key.split(/_(.+)/); // split on first underscore
      const emp = empById[empId];
      if (!empDays[empId]) {
        const name = emp?.firstName ? `${emp.firstName} ${emp.lastName}` : (emp?.name || empId);
        empDays[empId] = { empId, displayName: name, position: emp?.position || '', days: [] };
      }
      const scheduledStart = scheduled[key];
      const actualIn       = clocked[key] || null;
      const deltaMin       = actualIn ? Math.round((actualIn - scheduledStart) / 60_000) : null;
      empDays[empId].days.push({ date, scheduledStart, actualIn, deltaMin, noShow: !actualIn });
    });

    return Object.values(empDays)
      .map(e => {
        const compared = e.days.filter(d => d.deltaMin !== null);
        const lateCount = compared.filter(d => d.deltaMin > 5).length;
        const avgDelta  = compared.length
          ? Math.round(compared.reduce((s, d) => s + d.deltaMin, 0) / compared.length)
          : null;
        return { ...e, compared: compared.length, lateCount, avgDelta, noShows: e.days.filter(d => d.noShow).length };
      })
      .sort((a, b) => b.lateCount - a.lateCount || b.noShows - a.noShows);
  }, [scheduleShifts, timesheets, empById]);

  // ── Render ───────────────────────────────────────────────────────────────────
  if (periods.length === 0 && !loadingTS) {
    return (
      <div className="flex items-center justify-center h-40 border-2 border-dashed border-neutral-200 rounded-xl">
        <p className="text-sm text-neutral-400">No pay periods found. Set up payroll to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <select
          value={selectedId || ''}
          onChange={e => { setSelectedId(e.target.value); setExpandedId(null); }}
          className="bg-white border border-neutral-200 rounded-lg px-3 py-2 text-sm text-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-300 w-full sm:w-auto"
        >
          {periods.map(p => (
            <option key={p.id} value={p.id}>{parsePeriod(p.id).label}</option>
          ))}
        </select>

        <div className="flex bg-neutral-100 rounded-lg p-0.5 self-start">
          {[['hours', 'Hours'], ['punctuality', 'Punctuality']].map(([id, label]) => (
            <button
              key={id}
              onClick={() => { setActiveTab(id); setExpandedId(null); }}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                activeTab === id
                  ? 'bg-white text-neutral-900 shadow-sm'
                  : 'text-neutral-500 hover:text-neutral-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loadingTS ? (
        <p className="text-sm text-neutral-400 text-center py-10">Loading timesheets…</p>
      ) : activeTab === 'hours' ? (
        <HoursTab
          rows={hoursRows}
          expandedId={expandedId}
          setExpandedId={setExpandedId}
        />
      ) : (
        <PunctualityTab
          rows={punctualityRows}
          loading={loadingSched}
          expandedId={expandedId}
          setExpandedId={setExpandedId}
        />
      )}
    </div>
  );
};

export default TimesheetsView;
